import styled from "@emotion/styled";
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { formatNumber, i18n, persistor, ProductTypeCodeEnum, setProgressBarPercentage, useAppDispatch, usePaymentSlipAdditional } from 'src/common';
import { processQueryResult } from 'src/common/api/apollo';
import { useGetGeneralTransferPaymentDetailsQuery } from 'src/common/api/apollo/additional';
import { BarCode, Button, Card, Flex, Loader, Text } from 'src/components';
import { colors } from 'src/theme';

const CardContainerUp = styled(Flex)({
  padding: '8px 16px',
  backgroundColor: colors.primary1,
  borderRadius: '12px 12px 0 0'
})

const CardContainerDown = styled(Flex)({
  padding: '8px 16px',
  borderRadius: '12px'
})

const FlexCard = styled(Flex)({
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: colors.background6,
  width: '100%'
})

const FlexStyle = styled(Flex)({
  width: '100%'
})

const CheckoutPaymentSentPageAdditional = () => {
  const dispatch = useAppDispatch();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const { data: dataPaymentOptions } = useGetGeneralTransferPaymentDetailsQuery({
    // Treba poslati 12 i 1
    variables: { id: offerId, input: { installments: 1, type: ProductTypeCodeEnum.DODATNO } },
    fetchPolicy: 'cache-first',
    onCompleted: (result: any) => {
      processQueryResult(result);
    },
  });

  useEffect(() => {
    dispatch(setProgressBarPercentage(100));
    persistor.purge();
  }, []);

  const { downloadSlip, loading, PaymentSlip } = usePaymentSlipAdditional(offerId, 'oneTime');

  if (loading || !dataPaymentOptions || !dataPaymentOptions.getGeneralTransferPaymentDetails) return <Loader page />;

  // Definirajte podatke za prikaz u array-u
  const paymentDetails = [
    {
      label: i18n.t('pages.checkoutPaymentSentPage.payer'),
      value: `${dataPaymentOptions.getGeneralTransferPaymentDetails?.customerFirstName} ${dataPaymentOptions.getGeneralTransferPaymentDetails?.customerLastName}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.receiver'),
      value: (
        <Flex vertical align='flex-end'>
          <Text type="small" color={colors.text1}>{dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientName}</Text>
          <Text type="small" color={colors.text1}>{dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAddress}</Text>
          <Text type="small" color={colors.text1}>{`${dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientZipCode} ${dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientCity}`}</Text>
        </Flex>
      ),
      align: 'flex-start' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.amount'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.price ? `${formatNumber(dataPaymentOptions.getGeneralTransferPaymentDetails?.price)}€` : '',
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.referenceNumber'),
      value: `${dataPaymentOptions.getGeneralTransferPaymentDetails?.referencePrefix}${dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceNumberType === 'oib' ? dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceNumber : ''}${dataPaymentOptions.getGeneralTransferPaymentDetails?.referenceSuffix}`,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.model'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.model,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.description'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.description,
      align: 'center' as const
    },
    {
      label: i18n.t('pages.checkoutPaymentSentPage.iban'),
      value: dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAccountNumber,
      align: 'center' as const
    }
  ];

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <Text type="xxxLarge" bold>{i18n.t('pages.checkoutPaymentSentPage.payment')}</Text>
        <Text type="base">{i18n.t('pages.checkoutPaymentSentPage.paymentSubtitle')}</Text>
      </Flex>
      <Card padding='0px' radius={12}>
        <CardContainerUp justify='space-between'>
          <Text type="base" color={colors.white}>{i18n.t('pages.checkoutPaymentSentPage.toPay')}</Text>
          <Text type="base" color={colors.white} bold>{dataPaymentOptions.getGeneralTransferPaymentDetails?.price ? `${formatNumber(dataPaymentOptions.getGeneralTransferPaymentDetails?.price)}€` : ''}</Text>
        </CardContainerUp>
        <CardContainerDown vertical gap={24}>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.barcodePayment')}</Text>
            <BarCode price={formatNumber(dataPaymentOptions.getGeneralTransferPaymentDetails?.price || 0)} userFirstName={dataPaymentOptions.getGeneralTransferPaymentDetails?.customerFirstName || ''} userLastName={dataPaymentOptions.getGeneralTransferPaymentDetails?.customerLastName || ''} userAddress={dataPaymentOptions.getGeneralTransferPaymentDetails?.customerAddress || ''} userCity={dataPaymentOptions.getGeneralTransferPaymentDetails?.customerCity || ''} oib={dataPaymentOptions.getGeneralTransferPaymentDetails?.customerOib || ''} recipient={dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientName || ''} recipientAddress={dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAddress || ''} recipientCity={dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientCity || ''} model={dataPaymentOptions.getGeneralTransferPaymentDetails?.model || ''} iban={dataPaymentOptions.getGeneralTransferPaymentDetails?.recipientAccountNumber || ''} description={dataPaymentOptions.getGeneralTransferPaymentDetails?.description || ''} hidden={false} />
          </Flex>
          <Flex vertical gap={8} align='center'>
            <Text type="small" color={colors.text1}>{i18n.t('pages.checkoutPaymentSentPage.or')}</Text>
          </Flex>
          <Flex vertical gap={8} align='center'>
            <PaymentSlip />
            <Button btnType='textBlack' size='small' onClick={() => downloadSlip()}>{i18n.t('pages.checkoutPaymentSentPage.downloadSlip')}</Button>
            <FlexStyle vertical gap={2} align='center'>
              {paymentDetails.map((detail, index) => (
                <FlexCard key={index} gap={16} align={detail.align} justify='space-between'>
                  <Text type="tiny" color={colors.text4}>{detail.label}</Text>
                  {typeof detail.value === 'string' ? (
                    <Text type="small" color={colors.text1}>{detail.value}</Text>
                  ) : (
                    detail.value
                  )}
                </FlexCard>
              ))}
            </FlexStyle>
          </Flex>

        </CardContainerDown>
      </Card>
    </Flex>

  );
}

export default CheckoutPaymentSentPageAdditional;
